<template>
  <v-app>
    <router-view />
  </v-app>
</template>

<script lang="ts">
import { defineComponent, watch, type ComponentOptions } from 'vue';
import { findMeta, UILocale } from '@/shared';
import { RouteNames } from '@/router/routeNames';
import { type Item } from '@primerica/onex-ui-lib';
import { mdiChevronLeft } from '@mdi/js';
import { Translations } from '@/i18n/models';
import { useI18n } from 'vue-i18n';
import { useGlobalStore } from './stores/global/global.store';
import { mapStores } from 'pinia';

const componentOptions: ComponentOptions = {
  name: 'App',
  // See if this setup can be removed. There seems to be no difference if this is here or not
  setup() {
    const { locale } = useI18n();
    const globalStore = useGlobalStore();
    locale.value = globalStore.locale.replace('_', '-'); // Initialize locale
    watch(
      () => globalStore.locale,
      (newLocale) => {
        if (locale.value !== newLocale.replace('_', '-')) {
          locale.value = newLocale.replace('_', '-');
        }
      }
    );
    return {};
  },
  data() {
    return {
      mdiChevronLeft,
      RouteNames: RouteNames,
      Translations: Translations,
    };
  },
  computed: {
    ...mapStores(useGlobalStore),
    pageTitle(): string {
      const pageTitleStringId =
        findMeta(this.$route, 'pageTitle') ?? (this.$route.name as RouteNames | undefined);
      const appTitle = this.$t(Translations.app_title) || 'Starter App';
      const pageTitle = pageTitleStringId ? this.$t(pageTitleStringId) : '';
      const title = document.querySelector('title');
      if (title) {
        title.textContent = `${appTitle}${pageTitle ? ': ' : ''}${pageTitle}`;
      }

      return pageTitleStringId ? this.$t(pageTitleStringId) : '';
    },
    locales(): Item[] {
      return [
        {
          title: this.$t(Translations.languageToggle_enCA),
          value: UILocale.EN_CA,
        },
        {
          title: this.$t(Translations.languageToggle_enUS),
          value: UILocale.EN_US,
        },
        {
          title: this.$t(Translations.languageToggle_frCA),
          value: UILocale.FR_CA,
        },
        {
          title: this.$t(Translations.languageToggle_esUS),
          value: UILocale.ES_US,
        },
      ];
    },
  },
};

export default defineComponent(componentOptions);
</script>

<style lang="scss" scoped>
#app {
  padding-top: env(safe-area-inset-top);
  padding-right: env(safe-area-inset-right);
  padding-bottom: env(safe-area-inset-bottom);
  padding-left: env(safe-area-inset-left);
  /* Add other general styles like min-height if needed */
  min-height: 100vh; /* Use 100vh for standard height */
}
</style>
